import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { membershipKeys } from '@/hooks/useMembership';

/* How old the status may get before home re-reads it. */
export const STATUS_MAX_AGE_MS = 5 * 60 * 1000;
const CHECK_EVERY_MS = 60 * 1000;

/* For a future native build to fire on app foreground; harmless until then. */
export const NATIVE_RESUME_EVENT = 'teebox:resume';

/**
 * Keeps the status that decides home's experience (past-due gate, banners,
 * presale/booking-open) fresh inside the native WebView, where the member
 * cannot hard-reload.
 *
 * Re-reads only when the cached status is older than STATUS_MAX_AGE_MS, so
 * arriving on home repeatedly costs nothing. Checked on arrival, on resume
 * (iOS raises `visibilitychange`), and on a timer while home is open — the
 * timer is what covers Android, whose WebView does not reliably report resume.
 */
export function useLiveMemberStatus() {
  const queryClient = useQueryClient();

  useEffect(() => {
    const refreshIfStale = () => {
      if (document.visibilityState !== 'visible') return;
      const updatedAt = queryClient.getQueryState(['memberProfile'])?.dataUpdatedAt ?? 0;
      // No data yet means the first load is already on its way.
      if (!updatedAt || Date.now() - updatedAt < STATUS_MAX_AGE_MS) return;
      queryClient.invalidateQueries({ queryKey: ['memberProfile'] });
      queryClient.invalidateQueries({ queryKey: membershipKeys.all });
      queryClient.invalidateQueries({ queryKey: ['user-location'] });
      queryClient.invalidateQueries({ queryKey: ['locations'] });
    };

    refreshIfStale();
    const timer = setInterval(refreshIfStale, CHECK_EVERY_MS);
    document.addEventListener('visibilitychange', refreshIfStale);
    window.addEventListener('pageshow', refreshIfStale);
    window.addEventListener('focus', refreshIfStale);
    window.addEventListener(NATIVE_RESUME_EVENT, refreshIfStale);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', refreshIfStale);
      window.removeEventListener('pageshow', refreshIfStale);
      window.removeEventListener('focus', refreshIfStale);
      window.removeEventListener(NATIVE_RESUME_EVENT, refreshIfStale);
    };
  }, [queryClient]);
}
